import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import styles from './index.module.css';
import { GroupProduct, Product, ProductType, RedEnvelopePromotion } from '@meitu/subscribe-core';
import VipSwiper from '@/assets/js/swiper-bundle';
import {
  getDisplayPrice,
  getDurationPeriod,
  getIncomeSource,
  getSubscriptionDurationPeriodText,
} from '@/utils/vip';
import { getSkuType, getUrlParam, shouldShowRedEnvelopePopup } from '@/utils';
import {
  SubscribeType,
  isTeamSubscription,
  subscribeType,
  purchaseScene,
  PurchaseScene,
} from '@/constants';
import { PromotionType, RedEnvelopeType, TabType } from '@/constants/subscription';
import dayjs from 'dayjs';
import { CountUp } from 'countup.js';
import duration from 'dayjs/plugin/duration';
import { track } from '@/utils/common';
import {
  activeIndexAtom,
  activeProductMotionAtom,
  curTabAtom,
  loadingAtom,
  productsAtom,
  redEnvelopePromotionInfoAtom,
  seatsAtom,
  showNoticeBarAtom,
  showTabsAtom,
  teamInfoAtom,
  vipInfoAtom,
} from '@/store';
import { useAtom } from 'jotai';
dayjs.extend(duration);

interface ICountdownProps {
  data: Product;
  onRefresh?: () => void;
}
const Countdown: React.FC<ICountdownProps> = (props) => {
  const { data, onRefresh } = props;

  const [text, setText] = useState<string>('');
  const timerRef = useRef<any>(null);
  const endTime = dayjs(Number((data as any).countdownEndTime));

  const updateCountdown = () => {
    const now = dayjs();
    const diff = endTime.diff(now); // 毫秒差

    if (diff < 1000) {
      // 倒计时小于1秒时更新商品列表
      onRefresh?.();
    }
    // 计算剩余时间
    const duration = dayjs.duration(diff);

    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    setText(
      `${days}天 ${`0${hours}`.slice(-2)}:${`0${minutes}`.slice(-2)}:${`0${seconds}`.slice(-2)}`
    );
  };

  useEffect(() => {
    updateCountdown();
    timerRef.current = setInterval(() => {
      updateCountdown();
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  return (
    <div className={styles.countdown}>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 18 18"
        fill="none"
      >
        <path
          d="M9.00004 14.4591C12.0155 14.4591 14.46 12.0145 14.46 8.99906C14.46 5.98359 12.0155 3.53906 9.00004 3.53906C5.98456 3.53906 3.54004 5.98359 3.54004 8.99906C3.54004 12.0145 5.98456 14.4591 9.00004 14.4591ZM9.42005 6.47891V8.57891H11.52C11.868 8.57891 12.15 8.86097 12.15 9.20891C12.15 9.55685 11.868 9.83891 11.52 9.83891H8.79005C8.62297 9.83891 8.46272 9.77253 8.34457 9.65439C8.22643 9.53624 8.16005 9.376 8.16005 9.20891V6.47891C8.16005 6.13097 8.44211 5.84891 8.79005 5.84891C9.13799 5.84891 9.42005 6.13097 9.42005 6.47891Z"
          fill="white"
        />
      </svg>
      <span>{text}</span>
    </div>
  );
};

interface IProductPriceProps {
  data: Product;
  activeProductMotion: boolean;
  isRedEnvelopeProduct: boolean;
  envelopeType?: number;
  shouldShowRedEnvelopeModal: boolean;
}
const ProductPrice: React.FC<IProductPriceProps> = (props) => {
  const {
    data,
    activeProductMotion,
    isRedEnvelopeProduct,
    envelopeType,
    shouldShowRedEnvelopeModal,
  } = props;
  const [show, setShow] = useState<boolean>(false);

  // web侧业务通过hideLowPriceTrialCopywriting来控制是否展示0.9卡片上是文案，PC端环境则全部隐藏
  // const hideLowPriceTrialCopywriting: any =
  //   +(getUrlParam('hideLowPriceTrialCopywriting') || '') || getIncomeSource() !== 'MTXX-WEB';
  // 12月份PC端内嵌的临时放开，冲刺完成OKR
  const hideLowPriceTrialCopywriting: any = +(getUrlParam('hideLowPriceTrialCopywriting') || '');

  // 取第一个优惠信息
  const promotionInfo: any = data?.promotions && data.promotions[0];
  // 是否是低价试用商品
  const isLowPriceRrialProduct =
    promotionInfo?.promotionType === PromotionType.LowPriceTrialPromotion;
  const payPrice =
    isLowPriceRrialProduct && !hideLowPriceTrialCopywriting
      ? getDisplayPrice(data).payPrice
      : getDisplayPrice(data).price;

  const originalPrice = getDisplayPrice(data).originalPrice;

  const priceRef = useRef<any>();
  const countUpRef = useRef<CountUp>();

  useEffect(() => {
    // 有开启红包功能才需要此动效
    if (isRedEnvelopeProduct && data && shouldShowRedEnvelopeModal) {
      // 价格优惠策略折扣/低价试用
      const startPrice =
        RedEnvelopeType.DiscountStrategy === envelopeType
          ? (data.promoteProductPrice?.originalPrice || 0) / 100
          : RedEnvelopeType.LowPriceTrial === envelopeType
          ? (data.promoteProductPrice?.price || 0) / 100
          : originalPrice;
      countUpRef.current = new CountUp(priceRef.current, payPrice, {
        startVal: startPrice,
        duration: 1,
        decimalPlaces: Number.isInteger(payPrice) ? 0 : 1,
      });

      setShow(startPrice === payPrice);
    }

    if (isRedEnvelopeProduct && !shouldShowRedEnvelopeModal) {
      setShow(true);
    }
  }, []);

  useEffect(() => {
    if (isRedEnvelopeProduct && activeProductMotion) {
      countUpRef.current?.start();
      setShow(true);
    }
  }, [activeProductMotion]);

  return (
    <>
      <span ref={priceRef} className={styles.priceNum}>
        {payPrice}
      </span>
      {isLowPriceRrialProduct &&
      !hideLowPriceTrialCopywriting &&
      (!isRedEnvelopeProduct || (isRedEnvelopeProduct && show)) ? (
        <span className={styles.priceDesc}>
          试用{promotionInfo?.promotionDuration?.duration}
          {getSubscriptionDurationPeriodText(promotionInfo?.promotionDuration?.period)}
        </span>
      ) : null}
    </>
  );
};

interface IProps {
  useMeidou?: boolean;
  onRefresh?: () => void;
}

const ProductList: React.FC<IProps> = (props) => {
  const { useMeidou, onRefresh } = props;
  const [showLeftPanel, setShowLeftPanel] = useState<boolean>(false);
  const [showRightPanel, setShowRightPanel] = useState<boolean>(false);
  const ref = useRef<any>();
  const type: any = getUrlParam('type');

  const [teamInfo] = useAtom(teamInfoAtom);
  const [, setSeats] = useAtom(seatsAtom);
  const [loading] = useAtom(loadingAtom);
  const [curTab] = useAtom(curTabAtom);
  const [products] = useAtom(productsAtom);
  const [activeIndex, setActiveIndex] = useAtom(activeIndexAtom);
  const [activeProductMotion] = useAtom(activeProductMotionAtom);
  const [redEnvelopePromotionInfo] = useAtom(redEnvelopePromotionInfoAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [showNoticeBar] = useAtom(showNoticeBarAtom);
  const [showTabs] = useAtom(showTabsAtom);

  // 当㓟tabs和公告栏时，展示小卡片高度
  const isShowSmallCard = showNoticeBar && showTabs;

  // 优惠商品 ids
  const redEnvelopeProductIds = useMemo(() => {
    return redEnvelopePromotionInfo?.redEnvelopePromotionList?.map((item) => item.productId);
  }, [redEnvelopePromotionInfo?.redEnvelopePromotionList]);

  useEffect(() => {
    if (ref.current && activeIndex > -1) {
      ref.current.slideTo(activeIndex);
    }
  }, [activeIndex]);

  useEffect(() => {
    if (!products.length) return;

    if (ref.current) ref.current.destroy();
    initSwiper(activeIndex);
  }, [products]);

  const handlePanelStatus = (swiper: any) => {
    if (swiper.isBeginning) {
      setShowLeftPanel(false);
    } else {
      setShowLeftPanel(true);
    }

    if (swiper.isEnd) {
      setShowRightPanel(false);
    } else {
      setShowRightPanel(true);
    }
  };

  const initSwiper = (initedIndex: number) => {
    if (ref.current) ref.current.destroy();

    ref.current = new VipSwiper('.swiper-container', {
      slidesPerView: 2.93,
      slidesPerGroup: 3,
      // spaceBetween: 12,
      initialSlide: initedIndex,
      allowTouchMove: false,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      on: {
        slideChangeTransitionEnd: function (swiper: any) {
          handlePanelStatus(swiper);
        },
      },
    });

    handlePanelStatus(ref.current);
  };

  const renderArrow = (showArrow: boolean) => {
    return showArrow ? (
      <>
        <div
          className={classNames(styles.leftPanel, showLeftPanel ? styles.show : styles.hide)}
        ></div>
        <div className={classNames('swiper-button-prev', styles.arrow)}></div>
        <div
          className={classNames(styles.rightPanel, showRightPanel ? styles.show : styles.hide)}
        ></div>
        <div className={classNames('swiper-button-next', styles.arrow, styles.arrowRight)}></div>
      </>
    ) : (
      <div className={classNames(styles.rightPanel)}></div>
    );
  };

  // 美豆类型且购买美豆tab
  const renderMeidouType = (data: Product) => {
    const count = data.meidouRights?.count || 0;
    // 大额美豆(大于等于10w)展示需要处理下
    const countText = count >= 100000 ? `${count / 10000}万` : count;
    return (
      <>
        <div className={styles.name}>{countText}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          <span className={styles.priceNum}>{getDisplayPrice(data).price}</span>
        </div>
        {/* 原价xxxxx */}
        {data?.productPrice.originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}
        {/* 促销文案 */}
        {data?.productDesc ? <div className={styles.tips}>{data.productDesc}</div> : null}

        {/* 左上角tag */}
        {data.commonDesc ? <div className={styles.tag}>{data.commonDesc}</div> : null}
      </>
    );
  };

  // 抠图单购商品
  const renderConsumableType = (data: Product) => {
    const priceUnit = data?.commodityConfig?.count
      ? getDisplayPrice(data).price / data?.commodityConfig?.count
      : 0;
    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          <span className={styles.priceNum}>{priceUnit.toFixed(2)}</span>/张
        </div>

        <div className={styles.desc} style={{ textDecoration: 'none' }}>{`${
          getDisplayPrice(data).moneySymbol || ''
        }${getDisplayPrice(data).price}`}</div>

        {/* 张数商品展示有效期 */}
        <div className={styles.tips}>{getDurationPeriod(data.commodityConfig)}</div>
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  // 续期/非续期商品
  const renderSubscriptionType = (data: Product) => {
    // 是否开启了红包活动
    const isRedEnvelopeProduct = !!redEnvelopeProductIds?.includes(data.productId);

    const envelopeType = redEnvelopePromotionInfo?.redEnvelopePromotionList.find(
      (item) => item.productId === data.productId
    )?.envelopeType;

    const shouldShowRedEnvelopeModal = shouldShowRedEnvelopePopup();

    // 如果开启了倒计时且存在优惠策略
    const showCountdown = data.countdownFlag && data.promotions.length;

    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          <ProductPrice
            data={data}
            activeProductMotion={activeProductMotion}
            isRedEnvelopeProduct={isRedEnvelopeProduct}
            envelopeType={envelopeType}
            shouldShowRedEnvelopeModal={shouldShowRedEnvelopeModal}
          />
        </div>

        {/* 如果开启了倒计时且存在优惠策略，则不展示划线价 */}
        {!showCountdown && getDisplayPrice(data).originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}
        {/* 如果开启了倒计时且存在优惠策略，则划线价位置展示商品文案横条 */}
        {showCountdown && data.productDesc ? (
          <div className={styles.desc} style={{ textDecoration: 'initial' }}>
            {data.productDesc}
          </div>
        ) : null}

        {/* 会员商品展示商品文案横条 */}
        {/* 有倒计时不展示 */}
        {data.productDesc && !data.countdownFlag ? (
          <div className={styles.tips}>{data.productDesc}</div>
        ) : null}
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}

        {/* 如果开启了倒计时且存在优惠策略，则展示倒计时 */}
        {showCountdown && (!shouldShowRedEnvelopeModal || activeProductMotion) ? (
          <Countdown data={data} onRefresh={onRefresh} />
        ) : null}
      </>
    );
  };

  const renderTeamSubscriptionType = (data: GroupProduct) => {
    // 取第一个优惠信息
    const promotionInfo: any = data?.promotions && data.promotions[0];
    // 是否是阶梯优惠商品类型
    const isTieredDiscounts = promotionInfo?.promotionType === PromotionType.TieredDiscounts;

    if (isTieredDiscounts) {
      return (
        <>
          <div className={styles.name}>{data.productName}</div>
          <div className={styles.price}>
            <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
            <span className={styles.priceNum}>{+(data.promoteProductPrice.price || 0) / 100}</span>
          </div>

          {getDisplayPrice(data).originalPrice && purchaseScene === PurchaseScene.NORMAL ? (
            <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
              (+(data.productPrice.price || 0) / 100) * (promotionInfo.enjoyNum || 0)
            }`}</div>
          ) : null}
          {/* 中台暂不支持，前端先写死 */}
          <div className={styles.tips}>企业商用授权</div>
          <div className={styles.tag}>限时优惠</div>
        </>
      );
    }

    // isProbation 标识团队商品是否为低价试用商品

    // if (!data.isProbation && !vipInfo?.is_vip && purchaseScene === PurchaseScene.NORMAL) {
    //   // 非低价试用商品，且购买会员场景(非提前续费场景)
    //   return (
    //     <>
    //       <div className={styles.name}>{data.productName}</div>
    //       <div className={styles.price}>
    //         <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
    //         {+(getDisplayPrice(data).price * data.seatInfo.lowerLimit).toFixed(2)}
    //       </div>

    //       {getDisplayPrice(data).originalPrice ? (
    //         <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${+(
    //           getDisplayPrice(data).originalPrice * data.seatInfo.lowerLimit
    //         ).toFixed(2)}`}</div>
    //       ) : null}

    //       {/* 会员商品展示商品文案横条 */}
    //       {data.productDesc ? <div className={styles.tips}>1-2人使用</div> : null}
    //       {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
    //     </>
    //   );
    // }

    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          <span className={styles.priceNum}>{+(getDisplayPrice(data).price / 12).toFixed(2)}</span>
          <span className={styles.unit}>/人/月</span>
        </div>

        {getDisplayPrice(data).originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}

        {/* 会员商品展示商品文案横条 */}
        {data.productDesc ? <div className={styles.tips}>{data.productDesc}</div> : null}
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  // 批处理的消耗商品
  const renderBatchType = (data: Product) => {
    return (
      <>
        <div className={styles.name}>{data.productName}</div>
        <div className={styles.price}>
          <span className={styles.symbol}>{getDisplayPrice(data).moneySymbol}</span>
          <span className={styles.priceNum}>{getDisplayPrice(data).price}</span>
        </div>
        {getDisplayPrice(data).originalPrice ? (
          <div className={styles.desc}>{`原价${getDisplayPrice(data).moneySymbol || ''}${
            getDisplayPrice(data).originalPrice
          }`}</div>
        ) : null}
        <div className={styles.tips}>{getDurationPeriod(data.commodityConfig)}</div>
        {data.promotionBanner ? <div className={styles.tag}>{data.promotionBanner}</div> : null}
      </>
    );
  };

  const renderProductContent = (data: Product) => {
    if (type === 'svip') {
      return renderBatchType(data);
    }
    // 美豆场景且购买埋点tab
    if (useMeidou && curTab === TabType.MEIDOU) {
      return renderMeidouType(data);
    }

    // 抠图单购，且购买张数入口
    if (subscribeType === SubscribeType.CONSUMABLE && data.productType === ProductType.Consumable) {
      return renderConsumableType(data);
    }

    // 团队订阅
    if (curTab === TabType.SUB && isTeamSubscription) {
      return renderTeamSubscriptionType(data as GroupProduct);
    }

    // 其他
    return renderSubscriptionType(data);
  };

  const handleSelectChange = (index: number) => {
    if (loading) return;

    setActiveIndex(index);

    const current = products[index] as Product;

    // 设置默认席位数为商品的最小席位数
    if (isTeamSubscription) {
      const lowerLimit = (current as GroupProduct)?.seatInfo?.lowerLimit;
      // 当前团队绑定的成员数
      const userCount = teamInfo?.member_detail?.user_count || 0;
      lowerLimit && setSeats(current.isProbation ? lowerLimit : Math.max(lowerLimit, userCount));
    }

    // 6是立即赠送，匹配会员买赠信息
    const promotion: any = current?.promotions?.find(
      (item) => item.promotionType === PromotionType.Accessory
    );
    // 普通会员商品配置的买赠活动信息
    const promotionGiftForConsumable = promotion?.promotionGifts?.find(
      (item: any) => item.productType === ProductType.Consumable
    );

    let buy_num: any = '';
    if (current?.productType === ProductType.Consumable) {
      // 购买张数
      buy_num = current?.commodityConfig?.count || current?.meidouRights?.count;
    } else if (promotionGiftForConsumable) {
      // 会员买赠
      buy_num = promotionGiftForConsumable?.commodityConfig?.count;
    }

    let params: any = {};
    if (getIncomeSource() === 'MTXX-WEB') {
      params = {
        class: 'name_id',
        name_id: current?.productId,
        country_code: current?.countryCode || '',
        sku_type: getSkuType(),
        buy_num,
      };
    } else {
      params = {
        category: current?.productName,
        product_id: current?.productId,
        country_code: current?.countryCode || '',
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });
  };

  const renderCard = (data: Product, index: number) => {
    // 是否开启了红包活动
    const isRedEnvelopeProduct = !!redEnvelopeProductIds?.includes(data.productId);
    return (
      <div
        className={classNames(styles.card, {
          [styles.smallCard]: isShowSmallCard,
          [styles.smallCard]: isShowSmallCard,
          [styles.proCard]: curTab === TabType.PRO_SUB,
          [styles.cardActived]: activeIndex === index,
          [styles.cardProActived]: activeIndex === index && curTab === TabType.PRO_SUB,
          [styles.redEnvelopeCard]: isRedEnvelopeProduct,
        })}
        id={`product-${data.productId}`}
        onClick={() => handleSelectChange(index)}
      >
        {renderProductContent(data)}
      </div>
    );
  };

  const isSkeleton = !Object.keys((products && products[0]) || {}).length;

  return (
    <div className={classNames('vip-product-list', styles.module)}>
      <div className={classNames('swiper-container')}>
        <div className="swiper-wrapper">
          {products.length
            ? products?.map((v, index) => {
                return (
                  <div key={`${v.productId}_${index}`} className={classNames('swiper-slide')}>
                    {renderCard(v, index)}
                  </div>
                );
              })
            : Array.from({ length: 4 }, () => ({})).map((v, index) => {
                return (
                  <div
                    key={`skeleton_${index}`}
                    className={classNames('swiper-slide', styles.skeletonSlide)}
                  >
                    <div
                      className={classNames(styles.skeleton, {
                        [styles.smallSkeleton]: isShowSmallCard,
                      })}
                    ></div>
                  </div>
                );
              })}
        </div>
      </div>
      {renderArrow(!isSkeleton && products.length > 3)}
    </div>
  );
};

export default ProductList;
